import { Fragment, useEffect, useState, useMemo } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import FilePreview from "../Common/FilePreview";

const BillingAdd = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const workOrderFromUrl = searchParams.get("workOrder") || "";
  const projectFromUrl = searchParams.get("project") || "";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [boqItems, setBoqItems] = useState([]);
  const [boqTotal, setBoqTotal] = useState(0);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [paymentGroups, setPaymentGroups] = useState([]);
  // groupStages: per-group selected stages { "groupIdx_N": [...stages], "default": [...stages] }
  const [groupStages, setGroupStages] = useState({});
  const [stagePercentage, setStagePercentage] = useState(0);
  const [allowedBillingAmount, setAllowedBillingAmount] = useState(0);
  const [reportItemsCache, setReportItemsCache] = useState([]);
  const [showStageModal, setShowStageModal] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [paymentMode, setPaymentMode] = useState("STANDARD");

  const [directStages, setDirectStages] = useState({
    installation: false,
    tc: false,
    handover: false,
  });

  const BOQ_PAGE_SIZE = 10;

  const [boqPage, setBoqPage] = useState(1);
  const stageToReportMap = {
    Supply: ["MIR"],
    Installation: ["JMR"],
    "Testing & Commissioning": ["TC"],
    Handover: ["Handover"],
  };

  const [billingMode, setBillingMode] = useState("BOQ");
  // BOQ | REPORT

  const [workOrders, setWorkOrders] = useState([]);
  const filteredWorkOrders = useMemo(() => {
    let activeProjectId = projectFromUrl;
    if (!activeProjectId && workOrderFromUrl && workOrders.length > 0) {
      const found = workOrders.find((w) => String(w._id) === String(workOrderFromUrl));
      activeProjectId = found?.project?._id || found?.project;
    }
    if (!activeProjectId) return workOrders;
    return workOrders.filter((wo) => {
      const pId = wo.project?._id || wo.project;
      return String(pId) === String(activeProjectId);
    });
  }, [workOrders, projectFromUrl, workOrderFromUrl]);
  const [offices, setOffices] = useState([]);
  const [currentWorkOrder, setCurrentWorkOrder] = useState(null);
  const [reports, setReports] = useState({
    MIR: [],
    JMR: [],
    TC: [],
    Handover: [],
  });

  const [form, setForm] = useState({
    invoiceNumber: "",
    workOrder: "",
    stages: [],
    linkedReports: [],

    lineItems: [],
    subTotal: 0,

    cgst: 0,
    sgst: 0,
    igst: 0,

    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,

    totalAmount: 0,

    status: "Draft",
    invoiceDocument: null,
    remarks: "",
  });

  const normalizeTerms = (terms) => (Array.isArray(terms) ? terms : []);

  // Resolve payment config: item-level → group → WO global
  const getPaymentConfigForBoq = (boq) => {
    const itemTerms = normalizeTerms(boq?.paymentTerms);
    if (itemTerms.length) {
      return {
        mode: boq.paymentMode || paymentMode || "STANDARD",
        terms: itemTerms,
        groupKey: null,
      };
    }

    // Group-level fallback
    if (boq?._id && paymentGroups.length) {
      const boqKey = String(boq._id);
      const gIdx = paymentGroups.findIndex((g) =>
        (g.boqItemIds || []).map(String).includes(boqKey)
      );
      if (gIdx >= 0 && normalizeTerms(paymentGroups[gIdx].paymentTerms).length) {
        return {
          mode: paymentGroups[gIdx].paymentMode || paymentMode || "STANDARD",
          terms: normalizeTerms(paymentGroups[gIdx].paymentTerms),
          groupKey: `group_${gIdx}`,
        };
      }
    }

    return {
      mode: paymentMode || "STANDARD",
      terms: paymentTerms,
      groupKey: "default",
    };
  };

  // Get effective selected stages for a BOQ item (group-aware)
  const getEffectiveStagesForBoq = (boq) => {
    if (!paymentGroups.length) return form.stages; // no groups → global
    const config = getPaymentConfigForBoq(boq);
    const key = config.groupKey || "default";
    return groupStages[key] || [];
  };

  const getTermForStage = (stage, boq = null) => {
    const config = getPaymentConfigForBoq(boq);
    const target = String(stage || "").trim().toLowerCase();
    return (config.terms || []).find((t) =>
      (t.stage && String(t.stage).trim().toLowerCase() === target) ||
      (t.name && String(t.name).trim().toLowerCase() === target)
    );
  };

  const getTotalPercentForStages = (selectedStages, boq = null) => {
    const effectiveStages = boq && paymentGroups.length
      ? getEffectiveStagesForBoq(boq)
      : (selectedStages || []);
    return effectiveStages.reduce(
      (sum, stage) => sum + Number(getTermForStage(stage, boq)?.percentage || 0),
      0,
    );
  };

  // All unique stages across groups for REPORT mode (stageToReportMap lookups)
  const allSelectedStages = paymentGroups.length
    ? [...new Set(Object.values(groupStages).flat())]
    : form.stages;

  // Stages for the global/fallback dropdown (no groups)
  const stages =
    paymentMode === "STANDARD"
      ? ["Supply", "Installation", "Testing & Commissioning", "Handover"]
      : paymentTerms.map((t) => t.name);

  // Get stage options for a specific group
  const getStagesForGroup = (gIdx) => {
    const g = paymentGroups[gIdx];
    if (!g) return stages;
    return g.paymentMode === "STANDARD"
      ? ["Supply", "Installation", "Testing & Commissioning", "Handover"]
      : normalizeTerms(g.paymentTerms).map((t) => t.name);
  };

  const getStagesForDefault = () => stages;

  const standardReportTypes = allSelectedStages.flatMap(
    (stage) => stageToReportMap[stage] || [],
  );
  const visibleReportTypes = standardReportTypes.length > 0
    ? [...new Set(standardReportTypes)]
    : ["MIR", "JMR", "TC", "Handover"];

  const reportTypes = ["MIR", "JMR", "TC", "Handover"];
  const units = [
    "mm",
    "cm",
    "pcs",
    "kg",
    "litre",
    "meter",
    "box",
    "sqft",
    "cft",
    "ton",
  ];
  const statuses = [
    "Draft",
    "Submitted",
    "Paid",
    "Partially Paid",
  ];

  // INIT LOAD
  useEffect(() => {
    fetchWorkOrders();
    fetchOffices();
    generateInvoiceNumber();

    // Auto select work order from URL
    if (workOrderFromUrl) {
      setForm((prev) => ({
        ...prev,
        workOrder: workOrderFromUrl,
      }));

      fetchBOQAndTerms(workOrderFromUrl);
      fetchReportsByProject(workOrderFromUrl);
    }
  }, []);

  // Reactive GST evaluation based on client state and office state
  useEffect(() => {
    if (!currentWorkOrder) return;
    const clientState = getClientState(currentWorkOrder);
    const officeState = getOfficeState(currentWorkOrder);

    if (clientState && officeState) {
      const { cgst, sgst, igst } = applyGST(clientState, officeState);
      setForm((prev) => {
        if (prev.cgst === cgst && prev.sgst === sgst && prev.igst === igst) {
          return prev;
        }
        return { ...prev, cgst, sgst, igst };
      });
    }
  }, [currentWorkOrder, offices]);

  useEffect(() => {
    calculateAmounts();
  }, [form.lineItems, form.cgst, form.sgst, form.igst]);

  // Stage percentage calculation (group-aware)
  useEffect(() => {
    let totalAllowed = 0;

    if (paymentGroups.length) {
      // Group mode — sum each group's selected stages × their items
      let totalGroupPercent = 0;
      let groupPercentCount = 0;

      paymentGroups.forEach((g, gIdx) => {
        const key = `group_${gIdx}`;
        const selectedStages = groupStages[key] || [];
        if (!selectedStages.length) return;
        const gTerms = normalizeTerms(g.paymentTerms);
        const gMode = g.paymentMode || "STANDARD";

        // Sum percentage for selected stages in this group
        selectedStages.forEach((stage) => {
          const term = gMode === "STANDARD"
            ? gTerms.find((t) => t.stage === stage)
            : gTerms.find((t) => t.name === stage);
          totalGroupPercent += Number(term?.percentage || 0);
          groupPercentCount++;
        });

        boqItems.forEach((boq) => {
          const boqKey = String(boq._id);
          const inThisGroup = (g.boqItemIds || []).map(String).includes(boqKey);
          if (!inThisGroup) return;
          const itemTotal = Number(boq.quantity || 0) * Number(boq.price || 0);
          selectedStages.forEach((stage) => {
            const term = gMode === "STANDARD"
              ? gTerms.find((t) => t.stage === stage)
              : gTerms.find((t) => t.name === stage);
            totalAllowed += (itemTotal * Number(term?.percentage || 0)) / 100;
          });
        });
      });

      // In group mode, stagePercentage is not meaningful globally
      // — each group has its own % structure. Show "-" via -1 sentinel.
      setStagePercentage(-1);

      // Default (ungrouped) items
      const defaultStages = groupStages["default"] || [];
      if (defaultStages.length) {
        const assignedIds = new Set(
          paymentGroups.flatMap((g) => (g.boqItemIds || []).map(String))
        );
        boqItems.forEach((boq) => {
          if (assignedIds.has(String(boq._id))) return;
          const itemTotal = Number(boq.quantity || 0) * Number(boq.price || 0);
          defaultStages.forEach((stage) => {
            const term = getTermForStage(stage, boq);
            totalAllowed += (itemTotal * Number(term?.percentage || 0)) / 100;
          });
        });
      }
    } else {
      // No groups — original logic
      if (!form.stages.length || !paymentTerms.length || boqTotal === 0) {
        setStagePercentage(0);
        setAllowedBillingAmount(0);
        return;
      }
      let totalPercent = 0;
      form.stages.forEach((stage) => {
        const globalPercent = Number(getTermForStage(stage)?.percentage || 0);
        totalPercent += globalPercent;
        boqItems.forEach((boq) => {
          const itemPercent = Number(getTermForStage(stage, boq)?.percentage || 0);
          const itemTotal = Number(boq.quantity || 0) * Number(boq.price || 0);
          totalAllowed += (itemTotal * itemPercent) / 100;
        });
      });
      setStagePercentage(totalPercent);
    }

    setAllowedBillingAmount(totalAllowed.toFixed(2));
  }, [form.stages, groupStages, paymentTerms, paymentGroups, boqTotal, paymentMode, boqItems]);

  const fetchBOQAndTerms = async (projectId) => {
    try {
      const [woRes, termsRes] = await Promise.all([
        api.get(`/work-orders/${projectId}`),
        api.get(`/work-orders/${projectId}/payment-terms`),
      ]);

      const boq = woRes.data.data.boqItems || [];
      setBoqItems(boq);

      // Total BOQ Value
      const total = boq.reduce(
        (sum, i) => sum + Number(i.quantity) * Number(i.price),
        0,
      );
      setBoqTotal(total);

      if (termsRes.data.success) {
        setPaymentMode(termsRes.data.data.paymentMode || "STANDARD");
        setPaymentTerms(termsRes.data.data.paymentTerms || []);
        setPaymentGroups(termsRes.data.data.paymentGroups || []);

        const woData = woRes.data.data;
        setCurrentWorkOrder(woData);

        const clientState = getClientState(woData);
        const officeState = getOfficeState(woData);
        const { cgst, sgst, igst } = applyGST(clientState, officeState);

        setForm((prev) => ({
          ...prev,
          cgst,
          sgst,
          igst,
        }));
      }
    } catch (err) {
      swal("Error", "Failed to load BOQ or Payment Terms", "error");
    }
  };

  const getClientState = (wo) => {
    return (
      wo?.project?.client?.billingAddress?.state ||
      wo?.project?.client?.shippingAddress?.state ||
      wo?.client?.billingAddress?.state ||
      wo?.project?.state ||
      ""
    );
  };

  const getOfficeState = (wo) => {
    const office = wo?.project?.office;
    let st = "";
    if (office && typeof office === "object") {
      st = office.state || "";
      if (!st && office.address) {
        const addr = office.address.toLowerCase();
        if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
        else if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
      }
    } else {
      const officeId = office?._id || office;
      const foundOffice = offices.find((o) => String(o._id) === String(officeId));
      if (foundOffice) {
        st = foundOffice.state || "";
        if (!st && foundOffice.address) {
          const addr = foundOffice.address.toLowerCase();
          if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
          else if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
        }
      }
    }
    return st;
  };

  const applyGST = (clientState, officeState) => {
    const normalize = (s) => (s || "").toString().trim().toLowerCase();
    const cState = normalize(clientState);
    const oState = normalize(officeState);

    if (cState && oState && cState === oState) {
      // SAME STATE → CGST + SGST
      return { cgst: 9, sgst: 9, igst: 0 };
    }
    // DIFFERENT STATE OR MISSING → IGST
    return { cgst: 0, sgst: 0, igst: 18 };
  };

  // LOAD OFFICES
  const fetchOffices = async () => {
    try {
      const res = await api.get("/offices");
      if (res.data.success && Array.isArray(res.data.data)) {
        setOffices(res.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch offices", error);
    }
  };

  // LOAD WORK ORDERS
  const fetchWorkOrders = async () => {
    try {
      const res = await api.get("/work-orders?limit=1000");
      if (res.data.success) {
        setWorkOrders(res.data.data);
      }
    } catch (error) {
      swal("Error", "Failed to fetch work orders", "error");
    }
  };

  // Fetch all MIR/JMR/TC/Handover for selected project
  const fetchReportsByProject = async (workOrderId) => {
    if (!workOrderId) return;

    try {
      const [mir, jmr, tc, hand, ms, billingsRes] = await Promise.all([
        api.get(`/mir?workOrder=${workOrderId}&limit=all`),
        api.get(`/jmr?workOrder=${workOrderId}&limit=all`),
        api.get(`/testing-commissioning?workOrder=${workOrderId}&limit=all`),
        api.get(`/handover?workOrder=${workOrderId}&limit=all`),
        api.get(`/measurement-sheets?workOrder=${workOrderId}&limit=all`),
        api.get(`/billing?workOrder=${workOrderId}&limit=all`),
      ]);

      const billedReportIds = new Set();
      if (billingsRes.data.success) {
        (billingsRes.data.data || []).forEach((b) => {
          (b.linkedReports || []).forEach((group) => {
            (group.reportIds || []).forEach((id) => billedReportIds.add(String(id)));
          });
        });
      }

      const extractApproved = (res, type) => {
        const data = res?.data?.data || [];

        const all = data.filter((r) => {
          if (type === "MIR" || type === "JMR") {
            return r.approvalStatus === "Approved";
          }

          if (type === "TC" || type === "Handover" || type === "MEASUREMENT_SHEET") {
            return r.status === "Approved";
          }

          return false;
        });

        // MARK AS BILLED
        return all.map((r) => ({
          ...r,
          isBilled: billedReportIds.has(String(r._id)),
        }));
      };

      setReports({
        MIR: extractApproved(mir, "MIR").map((r) => ({ ...r, actualType: "MIR" })),
        JMR: [
          ...extractApproved(jmr, "JMR").map((r) => ({ ...r, actualType: "JMR" })),
          ...extractApproved(ms, "MEASUREMENT_SHEET").map((r) => ({
            ...r,
            actualType: "MEASUREMENT_SHEET",
          })),
        ],
        TC: extractApproved(tc, "TC").map((r) => ({ ...r, actualType: "TC" })),
        Handover: extractApproved(hand, "Handover").map((r) => ({
          ...r,
          actualType: "Handover",
        })),
      });
    } catch (error) {
      console.error("Failed to fetch reports:", error);
      swal("Error", "Failed to load approved reports", "error");
    }
  };

  const fetchBillingSourceItems = async (reportType, reportId) => {
    try {
      const anyStageSelected = paymentGroups.length
        ? Object.values(groupStages).some((s) => s && s.length > 0)
        : form.stages.length > 0;

      if (!anyStageSelected) {
        swal(
          "Select Stage",
          "Please select at least one stage first",
          "warning",
        );
        return;
      }

      const res = await api.get(`/billing-source/${reportType}/${reportId}`);
      const items = res.data?.data?.billingItems || [];

      if (!items.length) {
        swal("No Items", "No billing items found for this report", "info");
        return;
      }

      const hasPaymentTermsForSelection = boqItems.some(
        (boq) => getTotalPercentForStages(form.stages, boq) > 0,
      );

      if (!hasPaymentTermsForSelection) {
        swal(
          "Invalid Stage",
          "No payment terms found for selected stages",
          "error",
        );
        return;
      }

      // [OK] Combine percentages
      // [OK] Format line items
      const formatted = items
        .map((item) => {
          let boq = boqItems.find(
            (b) => String(b._id) === String(item.boqItemId),
          );

          // Fallback: match by item name if ID fails
          if (!boq) {
            boq = boqItems.find(
              (b) =>
                b.itemName?.trim().toLowerCase() ===
                (item.itemName || item.description)?.trim().toLowerCase(),
            );
          }

          if (!boq) return null;

          const boqPrice = Number(boq.price || 0);
          const qty = Number(item.quantity || 0);

          const itemPercent = getTotalPercentForStages(form.stages, boq);
          const stageRate = (boqPrice * itemPercent) / 100;
          const amount = qty * stageRate;

          return {
            wlino: boq.wlino || "",

            hsnSac: boq.hsnSac || "",
            description: item.itemName || item.description,

            quantity: qty,
            unit: boq.unit || "pcs",
            rate: stageRate.toFixed(2),
            amount: amount.toFixed(2),
            boqItemId: boq._id,
            source: item.source,
          };
        })
        .filter(Boolean);

      // Cache raw report items (important for recalculation)
      setReportItemsCache(items);

      // Replace line items
      setForm((prev) => ({
        ...prev,
        lineItems: formatted,
      }));
    } catch (error) {
      console.error("Billing source fetch error:", error);
      swal(
        "Error",
        error.response?.data?.message || "Failed to fetch billing source items",
        "error",
      );
    }
  };

  const recalculateReportLineItems = (items = []) => {
    const anyStageSelected = paymentGroups.length
      ? Object.values(groupStages).some((s) => s && s.length > 0)
      : form.stages.length > 0;

    if (!boqItems.length || (!paymentTerms.length && !paymentGroups.length) || !anyStageSelected) return;

    const reportToStageMap = {
      MIR: "Supply",
      JMR: "Installation",
      MEASUREMENT_SHEET: "Installation",
      TC: "Testing & Commissioning",
      Handover: "Handover",
    };

    const cleanName = (str) =>
      str?.replace(/\[WLI-.*?\]\s*/i, "").replace(/\s+/g, " ").trim().toLowerCase();

    // 1. Get active BOQ items based on paymentGroups or global stages
    const activeBoqItems = paymentGroups.length
      ? boqItems.filter((b) => {
          const config = getPaymentConfigForBoq(b);
          const key = config.groupKey || "default";
          const selected = groupStages[key] || [];
          return selected.length > 0;
        })
      : boqItems;

    const consumedReportIndices = new Set();

    // 2. Map every active BOQ item, merging with reportItem if available
    const mergedItems = activeBoqItems.map((b) => {
      let stagePercent = 0;
      const config = getPaymentConfigForBoq(b);
      const effectiveStages = getEffectiveStagesForBoq(b);
      if (config.mode === "CUSTOM") {
        stagePercent = getTotalPercentForStages(effectiveStages, b);
      } else {
        const stdStage = effectiveStages.length > 0 ? effectiveStages[0] : "";
        const term = getTermForStage(stdStage, b);
        stagePercent = Number(term?.percentage || 0);
        if (stagePercent === 0) {
          stagePercent = getTotalPercentForStages(effectiveStages, b);
        }
      }

      const rate = (Number(b.price || 0) * stagePercent) / 100;
      const stage = effectiveStages.length > 0 ? effectiveStages.join(", ") : "";

      // Search matching item in report items
      let matchedRepItem = null;
      let matchedIdx = -1;

      for (let i = 0; i < (items || []).length; i++) {
        if (consumedReportIndices.has(i)) continue;
        const rep = items[i];

        // Match by BOQ ID
        if (rep.boqItemId && String(rep.boqItemId) === String(b._id)) {
          matchedRepItem = rep;
          matchedIdx = i;
          break;
        }

        // Match by wlino
        const repWlino = rep.wlino || rep.source?.wlino;
        if (repWlino && b.wlino && String(repWlino).trim() === String(b.wlino).trim()) {
          matchedRepItem = rep;
          matchedIdx = i;
          break;
        }

        // Match by [WLI-...] regex
        const text = rep.description || rep.itemName || "";
        const m = text.match(/\[WLI-(.*?)\]/i);
        if (m && m[1] && b.wlino && String(m[1]).trim() === String(b.wlino).trim()) {
          matchedRepItem = rep;
          matchedIdx = i;
          break;
        }

        // Match by clean name
        const repName = cleanName(rep.itemName || rep.description);
        const boqName = cleanName(b.itemName);
        if (repName && boqName && (repName === boqName || repName.includes(boqName) || boqName.includes(repName))) {
          matchedRepItem = rep;
          matchedIdx = i;
          break;
        }
      }

      if (matchedRepItem) {
        consumedReportIndices.add(matchedIdx);
        const existing = (form.lineItems || []).find((it) => (it.boqItemId && String(it.boqItemId) === String(b._id)) || (it.wlino && b.wlino && String(it.wlino).trim() === String(b.wlino).trim()));
        const repQty = (existing && existing.quantity !== undefined && existing.quantity !== null && existing.quantity !== "") ? Number(existing.quantity) : Number(matchedRepItem.quantity || 0);
        const repType = matchedRepItem.source?.reportType;
        const repStage = stage || (paymentMode === "STANDARD" ? reportToStageMap[repType] : "");

        return {
          wlino: b.wlino || matchedRepItem.wlino || "",
          hsnSac: b.hsnSac || matchedRepItem.hsnSac || "",
          description: b.itemName || matchedRepItem.itemName || matchedRepItem.description,
          quantity: repQty,
          unit: b.unit || matchedRepItem.unit || "Nos",
          rate: rate.toFixed(2),
          amount: (repQty * rate).toFixed(2),
          boqItemId: b._id,
          stage: stage || repStage,
          stagePercentage: stagePercent,
          billingType: "REPORT",
          source: matchedRepItem.source,
        };
      }

      // If not matched in report, keep BOQ item intact in the table!
      const existing = (form.lineItems || []).find((it) => (it.boqItemId && String(it.boqItemId) === String(b._id)) || (it.wlino && b.wlino && String(it.wlino).trim() === String(b.wlino).trim()));
      const boqQty = (existing && existing.quantity !== undefined && existing.quantity !== null && existing.quantity !== "") ? Number(existing.quantity) : Number(b.quantity || 0);
      return {
        wlino: b.wlino || "",
        hsnSac: b.hsnSac || "",
        description: b.itemName,
        quantity: boqQty,
        unit: b.unit || "Nos",
        rate: rate.toFixed(2),
        amount: (boqQty * rate).toFixed(2),
        boqItemId: b._id,
        stage,
        stagePercentage: stagePercent,
        billingType: "BOQ",
        source: null,
      };
    });

    // 3. Append any unmatched report items (extra manual items in report)
    (items || []).forEach((rep, idx) => {
      if (consumedReportIndices.has(idx)) return;
      const repType = rep.source?.reportType;
      const stdStage = reportToStageMap[repType] || "";
      const selectedCustomStage = form.stages?.[0] || Object.values(groupStages).flat()[0] || "";
      const repStage = selectedCustomStage || (paymentMode === "STANDARD" ? stdStage : "");
      const repQty = Number(rep.quantity || 0);
      const repRate = Number(rep.rate || 0);

      mergedItems.push({
        wlino: rep.wlino || "",
        hsnSac: rep.hsnSac || "",
        description: rep.itemName || rep.description || "",
        quantity: repQty,
        unit: rep.unit || "Nos",
        rate: repRate.toFixed(2),
        amount: (repQty * repRate).toFixed(2),
        boqItemId: rep.boqItemId || null,
        stage: repStage || stdStage || "",
        stagePercentage: 0,
        billingType: "REPORT",
        source: rep.source,
      });
    });

    setForm((prev) => ({
      ...prev,
      lineItems: mergedItems,
    }));
  };
  const groupItemsByReport = (items) => {
    const groups = {};

    items.forEach((item, index) => {
      const type = item.source?.reportType || "BOQ";
      const id = item.source?.reportId || "manual";
      const number = item.source?.reportNumber || null;

      const key = `${type}-${id}`;

      if (!groups[key]) {
        groups[key] = {
          reportType: type,
          reportId: id,
          reportNumber: number,
          items: [],
        };
      }

      groups[key].items.push({
        ...item,
        originalIndex: index,
      });
    });

    return Object.values(groups);
  };

  const reportToStage = {
    MIR: "Supply",
    JMR: "Installation",
    TC: "Testing & Commissioning",
    Handover: "Handover",
    MEASUREMENT_SHEET: "Installation",
    BOQ: "BOQ",
  };
  const reportOrder = ["MIR", "JMR", "TC", "Handover", "MEASUREMENT_SHEET", "BOQ"];

  const groupedItems = groupItemsByReport(form.lineItems).sort(
    (a, b) =>
      reportOrder.indexOf(a.reportType) - reportOrder.indexOf(b.reportType),
  );

  useEffect(() => {
    const anyStageSelected = paymentGroups.length
      ? Object.values(groupStages).some((s) => s && s.length > 0)
      : form.stages.length > 0;
    if (!anyStageSelected) {
      setForm((prev) => ({ ...prev, lineItems: [] }));
      return;
    }

    recalculateReportLineItems(reportItemsCache);
  }, [form.stages, groupStages, paymentTerms, paymentGroups, boqItems, paymentMode]);

  const loadNextBoqItems = () => {
    // All items for selected stages/groups are already loaded
  };

  // AUTO INVOICE NO
  const generateInvoiceNumber = async () => {
    try {
      const res = await api.get("/billing");

      if (res.data.success && res.data.data.length > 0) {
        const last = res.data.data[0];
        const match = last.invoiceNumber?.match(/INV(\d+)/);

        if (match) {
          const next = parseInt(match[1]) + 1;
          setForm((prev) => ({
            ...prev,
            invoiceNumber: `INV${String(next).padStart(5, "0")}`,
          }));
          return;
        }
      }

      setForm((prev) => ({ ...prev, invoiceNumber: "INV00001" }));
    } catch (error) {
      setForm((prev) => ({
        ...prev,
        invoiceNumber: `INV${Date.now().toString().slice(-5)}`,
      }));
    }
  };

  const calculateAmounts = () => {
    const subTotal = form.lineItems.reduce(
      (sum, item) =>
        sum + parseFloat(item.quantity || 0) * parseFloat(item.rate || 0),
      0,
    );

    const cgstAmount = (subTotal * form.cgst) / 100;
    const sgstAmount = (subTotal * form.sgst) / 100;
    const igstAmount = (subTotal * form.igst) / 100;

    const totalAmount = subTotal + cgstAmount + sgstAmount + igstAmount;

    setForm((prev) => ({
      ...prev,
      subTotal: subTotal.toFixed(2),
      cgstAmount: cgstAmount.toFixed(2),
      sgstAmount: sgstAmount.toFixed(2),
      igstAmount: igstAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    }));
  };

  // HANDLE FORM CHANGE
  const handleChange = async (e) => {
    const { name, value } = e.target;

    /* ======================================================
    WORK ORDER CHANGE (Previously called "project")
    ====================================================== */
    if (name === "workOrder") {
      setBillingMode("BOQ");
      // Clear selection
      if (!value) {
        setForm((prev) => ({
          ...prev,
          workOrder: "",

          cgst: 0,
          sgst: 0,
          igst: 0,
          cgstAmount: 0,
          sgstAmount: 0,
          igstAmount: 0,

          lineItems: [
            {
              wlino: "",
              hsnSac: "",

              description: "",
              quantity: 0,
              unit: "pcs",
              rate: 0,
              amount: 0,
            },
          ],
        }));

        setBoqItems([]);
        setPaymentTerms([]);
        setPaymentGroups([]);
        setGroupStages({});
        setBoqTotal(0);
        setStagePercentage(0);
        setAllowedBillingAmount(0);
        setReports({ MIR: [], JMR: [], TC: [], Handover: [], MEASUREMENT_SHEET: [] });

        swal("Work Order Cleared", "Please select a work order", "info");
        return;
      }

      // Set work order
      setForm((prev) => ({
        ...prev,
        workOrder: value,
        linkedReports: [],

        lineItems: [
          {
            wlino: "",
            hsnSac: "",
            description: "",
            quantity: 0,
            unit: "pcs",
            rate: 0,
            amount: 0,
          },
        ],
      }));

      swal({
        title: "Loading Work Order Data",
        text: "Fetching BOQ, Payment Terms & Reports...",
        icon: "info",
        buttons: false,
        closeOnClickOutside: false,
      });

      try {
        await fetchBOQAndTerms(value);
        await fetchReportsByProject(value);

        swal.close();
        swal("Success", "Work order data loaded successfully", "success");
      } catch (err) {
        swal.close();
        swal("Error", "Failed to load work order data", "error");
      }

      return;
    }

    /* ======================================================
    TAX PERCENTAGE
    ====================================================== */
    if (name === "taxPercentage") {
      setForm((prev) => ({
        ...prev,
        taxPercentage: Number(value),
      }));
      return;
    }

    /* ======================================================
    STATUS / REMARKS / NORMAL FIELDS
    ====================================================== */
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const openStageModal = (index) => {
    setActiveItemIndex(index);

    setDirectStages({
      installation: false,
      tc: false,
      handover: false,
    });

    setShowStageModal(true);
  };
  const applyDirectStages = () => {
    if (activeItemIndex === null) return;

    // [OK] Must select one option
    if (
      !directStages.installation &&
      !directStages.tc &&
      !directStages.handover
    ) {
      swal("Select Stage", "Please choose a direct stage", "warning");
      return;
    }

    const item = form.lineItems[activeItemIndex];

    const boq = boqItems.find((b) => String(b._id) === String(item.boqItemId));

    if (!boq) return;

    const stageOrder = [
      "Supply",
      "Installation",
      "Testing & Commissioning",
      "Handover",
    ];

    let targetStage = null;

    if (directStages.handover) targetStage = "Handover";
    else if (directStages.tc) targetStage = "Testing & Commissioning";
    else if (directStages.installation) targetStage = "Installation";

    // [OK] Stage must be selected in billing stages
    if (!form.stages.includes(targetStage)) {
      swal(
        "Invalid Stage",
        `${targetStage} is not selected in Billing Stages`,
        "error",
      );
      return;
    }

    const stageIndex = stageOrder.indexOf(targetStage);

    let totalPercent = 0;

    for (let i = 0; i <= stageIndex; i++) {
      const stageName = stageOrder[i];

      if (!form.stages.includes(stageName)) continue;

      const term = getTermForStage(stageName, boq);

      if (term) totalPercent += Number(term.percentage || 0);
    }

    const rate = (Number(boq.price) * totalPercent) / 100;
    const qty = Number(item.quantity || 0);

    const updated = [...form.lineItems];

    updated[activeItemIndex] = {
      ...item,
      rate: rate.toFixed(2),
      amount: (qty * rate).toFixed(2),
      directStage: targetStage,
    };

    setForm((prev) => ({
      ...prev,
      lineItems: updated,
    }));

    setShowStageModal(false);
  };

  // Line Items Change
  const handleLineItemChange = (index, field, value) => {
    const items = [...form.lineItems];
    items[index][field] = value;

    if (field === "quantity" || field === "rate") {
      const qty = parseFloat(items[index].quantity || 0);
      const rate = parseFloat(items[index].rate || 0);
      items[index].amount = (qty * rate).toFixed(2);
    }

    setForm((prev) => ({ ...prev, lineItems: items }));
  };

  const addLineItem = () => {
    setForm((prev) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        {
          wlino: "",
          hsnSac: "",
          description: "",
          quantity: 0,
          unit: "pcs",
          rate: 0,
          amount: 0,
        },
      ],
    }));
  };

  const removeLineItem = (index) => {
    if (form.lineItems.length === 1) {
      swal("Not Allowed", "At least one line item is required", "warning");
      return;
    }

    setForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };

  // Upload File
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = () => {
      setForm((prev) => ({
        ...prev,
        invoiceDocument: {
          fileName: file.name,
          fileUrl: reader.result,
        },
      }));
    };

    reader.readAsDataURL(file);
  };

  // SUBMIT FORM
  const handleSubmit = async (e) => {
    e.preventDefault();

    // BASIC VALIDATION
    if (!form.invoiceNumber || !form.workOrder) {
      swal(
        "Missing Information",
        "Invoice number and project are required",
        "warning",
      );
      return;
    }

    if (Number(form.subTotal) > Number(allowedBillingAmount)) {
      swal(
        "Billing Limit Exceeded",
        `You can bill maximum ₹${allowedBillingAmount} for ${form.stages.join(", ")}
`,
        "error",
      );
      return;
    }

    // AT LEAST ONE LINE ITEM CHECK
    if (!form.lineItems || form.lineItems.length === 0) {
      swal("No Line Items", "Please add at least one billing item", "warning");
      return;
    }

    // SHOW LOADING
    swal({
      title: "Creating Billing",
      text: "Please wait while we save the invoice...",
      icon: "info",
      buttons: false,
      closeOnClickOutside: false,
    });

    try {
      setLoading(true);

      const payload = {
        workOrder: form.workOrder,

        // In group mode, flatten all selected stages across groups
        groupStages: paymentGroups.length ? groupStages : undefined,
        stages: paymentGroups.length
          ? [...new Set(Object.values(groupStages).flat())]
          : form.stages,

        linkedReports: (form.linkedReports || []).flatMap((g) => {
          const typeMap = {};
          (g.reportIds || []).forEach((id) => {
            const report = reports[g.reportType]?.find((r) => r._id === id);
            const actualType = report?.actualType || g.reportType;
            if (!typeMap[actualType]) typeMap[actualType] = [];
            typeMap[actualType].push(id);
          });

          return Object.keys(typeMap).map((type) => ({
            reportType: type,
            reportIds: typeMap[type],
          }));
        }),

        lineItems: (form.lineItems || []).map((item) => ({
          wlino: item.wlino || "",
          hsnSac: item.hsnSac || "",

          description: item.description,
          quantity: Number(item.quantity || 0),
          unit: item.unit,
          rate: Number(item.rate || 0),
          amount: Number(item.amount || 0),
          boqItemId: item.boqItemId || undefined,
          stage: item.stage || undefined,
          stagePercentage: Number(item.stagePercentage || 0),
          directStage: item.directStage || undefined,

          source: item.source
            ? {
              reportType: item.source.reportType,
              reportId: item.source.reportId,
              itemId: item.source.itemId,
            }
            : undefined,
        })),

        subTotal: Number(form.subTotal || 0),

        cgst: Number(form.cgst || 0),
        sgst: Number(form.sgst || 0),
        igst: Number(form.igst || 0),

        cgstAmount: Number(form.cgstAmount || 0),
        sgstAmount: Number(form.sgstAmount || 0),
        igstAmount: Number(form.igstAmount || 0),

        totalAmount: Number(form.totalAmount || 0),

        status: form.status || "Draft",

        invoiceDocument: form.invoiceDocument || null,
        remarks: form.remarks || "",
      };

      const res = await api.post("/billing", payload);

      swal.close(); // [OK] close loader

      if (res.data.success) {
        swal({
          title: "Billing Created",
          text: "Invoice has been created successfully",
          icon: "success",
          buttons: {
            cancel: "Stay Here",
            confirm: {
              text: "Go to Billing List",
              value: true,
            },
          },
        }).then((go) => {
          if (go) {
            navigate(
              workOrderFromUrl
                ? `/billings?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                : "/billings"
            );
          }
        });
      }
    } catch (error) {
      swal.close();

      swal(
        "Billing Failed",
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to create billing",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // UI
  return (
    <Fragment>
      <PageTitle activeMenu="Add Billing" motherMenu="Billing Management" />

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title>Add New Billing/Invoice</Card.Title>
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    workOrderFromUrl
                      ? `/billings?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                      : "/billings",
                  )
                }
              >
                <i className="fas fa-arrow-left me-2" /> Back
              </Button>
            </Card.Header>

            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {/* BASIC INFO */}
                <div className="border-bottom pb-3 mb-4">
                  <h5>
                    <i className="fas fa-info-circle me-2"></i>Basic Information
                  </h5>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Invoice Number</Form.Label>
                        <Form.Control
                          type="text"
                          value={form.invoiceNumber}
                          readOnly
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          name="status"
                          value={form.status}
                          onChange={handleChange}
                        >
                          {statuses.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Work Order</Form.Label>
                        <Form.Select
                          name="workOrder"
                          value={form.workOrder}
                          onChange={handleChange}
                        >
                          <option value="">Select Work Order</option>

                          {filteredWorkOrders.map((wo) => (
                            <option key={wo._id} value={wo._id}>
                              {wo.woNo}{wo.label ? ` – ${wo.label}` : ""} —{" "}
                              {wo.project?.projectName || wo.project?.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Stage</Form.Label>

                        {paymentGroups.length > 0 ? (
                          <div className="border rounded p-2" style={{ background: "#f8f9fa" }}>
                            {paymentGroups.map((g, gIdx) => {
                              const key = `group_${gIdx}`;
                              const opts = getStagesForGroup(gIdx);
                              return (
                                <div key={gIdx} className="d-flex align-items-center gap-2 mb-2">
                                  <span className="badge bg-primary" style={{ minWidth: 80, fontSize: 11 }}>
                                    {g.groupName}
                                  </span>
                                  <div style={{ flex: 1 }}>
                                    <Select
                                      isMulti
                                      options={opts.map((s) => ({ value: s, label: s }))}
                                      value={(groupStages[key] || []).map((s) => ({ value: s, label: s }))}
                                      onChange={(sel) => {
                                        const selected = sel ? sel.map((o) => o.value) : [];
                                        setGroupStages((prev) => ({ ...prev, [key]: selected }));
                                        if (form.workOrder) fetchReportsByProject(form.workOrder);
                                      }}
                                      placeholder="Select stages..."
                                      classNamePrefix="react-select"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            {boqItems.some((b) => {
                              const assignedIds = new Set(paymentGroups.flatMap((g) => (g.boqItemIds || []).map(String)));
                              return !assignedIds.has(String(b._id));
                            }) && (
                                <div className="d-flex align-items-center gap-2">
                                  <span className="badge bg-secondary" style={{ minWidth: 80, fontSize: 11 }}>Default</span>
                                  <div style={{ flex: 1 }}>
                                    <Select
                                      isMulti
                                      options={getStagesForDefault().map((s) => ({ value: s, label: s }))}
                                      value={(groupStages["default"] || []).map((s) => ({ value: s, label: s }))}
                                      onChange={(sel) => {
                                        const selected = sel ? sel.map((o) => o.value) : [];
                                        setGroupStages((prev) => ({ ...prev, default: selected }));
                                      }}
                                      placeholder="Select stages..."
                                      classNamePrefix="react-select"
                                    />
                                  </div>
                                </div>
                              )}
                          </div>
                        ) : (
                          <Select
                            isMulti
                            options={stages.map((st) => ({ value: st, label: st }))}
                            value={form.stages?.map((st) => ({ value: st, label: st })) || []}
                            onChange={(selectedOptions) => {
                              const selected = selectedOptions ? selectedOptions.map(o => o.value) : [];
                              setForm((prev) => ({ ...prev, stages: selected }));
                              if (form.workOrder) fetchReportsByProject(form.workOrder);
                            }}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* LINKED REPORT */}
                <div className="border-bottom pb-3 mb-4">
                    <h5>
                      <i className="fas fa-link me-2"></i>Linked Report
                      (Optional)
                    </h5>

                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Report Type</Form.Label>

                          <Select
                            isMulti
                            options={visibleReportTypes.map((type) => ({ value: type, label: type }))}
                            value={form.linkedReports.map((r) => ({ value: r.reportType, label: r.reportType }))}
                            onChange={(selectedOptions) => {
                              const selectedTypes = selectedOptions ? selectedOptions.map(o => o.value) : [];

                              const updated = selectedTypes.map((type) => ({
                                reportType: type,
                                reportIds: [],
                              }));

                              setForm((prev) => ({
                                ...prev,
                                linkedReports: updated,
                              }));

                              const mode =
                                updated.length > 0 ? "REPORT" : "BOQ";
                              setBillingMode(mode);

                              if (updated.length === 0) {
                                setReportItemsCache([]);
                                recalculateReportLineItems([]);
                              }

                              fetchReportsByProject(form.workOrder);
                            }}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        {form.linkedReports.map((group, index) => (
                          <Form.Group key={group.reportType} className="mb-3">
                            <Form.Label>{group.reportType} Reports</Form.Label>

                            <Select
                              isMulti
                              options={reports[group.reportType]?.map((r) => ({
                                value: r._id,
                                label: `${r.mirNumber || r.jmrNumber || r.tcNumber || r.hoNumber || r.sheetNumber || r._id} ${r.isBilled ? "(Billed)" : ""}`
                              })) || []}
                              value={(group.reportIds || []).map(id => {
                                const r = reports[group.reportType]?.find(rep => rep._id === id);
                                return {
                                  value: id,
                                  label: r ? `${r.mirNumber || r.jmrNumber || r.tcNumber || r.hoNumber || r.sheetNumber || r._id} ${r.isBilled ? "(Billed)" : ""}` : id
                                };
                              })}
                              onChange={async (selectedOptions) => {
                                const selectedIds = selectedOptions ? selectedOptions.map(o => o.value) : [];

                                const updated = [...form.linkedReports];
                                updated[index].reportIds = selectedIds;

                                setForm((prev) => ({
                                  ...prev,
                                  linkedReports: updated,
                                }));

                                const anyStageSelected = paymentGroups.length
                                  ? Object.values(groupStages).some((s) => s && s.length > 0)
                                  : form.stages.length > 0;

                                if (!anyStageSelected) {
                                  swal(
                                    "Select Stage First",
                                    "Please select at least one stage before selecting reports",
                                    "warning",
                                  );
                                  return;
                                }

                                try {
                                  // Collect ALL items from ALL report groups
                                  let allItems = [];

                                  for (const g of updated) {
                                    for (const id of g.reportIds) {
                                      const report = reports[g.reportType]?.find(r => r._id === id);
                                      const actualType = report?.actualType || g.reportType;

                                      const res = await api.get(
                                        `/billing-source/${actualType}/${id}`,
                                      );

                                      const items =
                                        res.data?.data?.billingItems || [];
                                      allItems = [...allItems, ...items];
                                    }
                                  }

                                  if (!allItems.length) {
                                    setReportItemsCache([]);
                                    recalculateReportLineItems([]);
                                    return;
                                  }

                                  setReportItemsCache(allItems);
                                  recalculateReportLineItems(allItems);
                                } catch (error) {
                                  swal(
                                    "Error",
                                    error.response?.data?.message ||
                                    "Failed to fetch report items",
                                    "error",
                                  );
                                }
                              }}
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </Form.Group>
                        ))}
                      </Col>
                    </Row>
                  </div>

                {/* LINE ITEMS */}
                <div className="border-bottom pb-3 mb-4">
                  <div className="d-flex justify-content-between mb-3">
                    <h5>
                      <i className="fas fa-list me-2"></i>Line Items
                    </h5>
                    <Button size="sm" onClick={addLineItem}>
                      + Add Item
                    </Button>
                  </div>
                  {billingMode === "BOQ" &&
                    form.lineItems.length < boqItems.length && (
                      <div className="text-center mt-3">
                        <Button
                          variant="outline-primary"
                          onClick={loadNextBoqItems}
                        >
                          + Load Next BOQ Items
                        </Button>
                        <div className="text-muted mt-1">
                          Showing {form.lineItems.length} of {boqItems.length}{" "}
                          BOQ items
                        </div>
                      </div>
                    )}

                  <div className="table-responsive">
                    <Table bordered>
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>WLINo</th>
                          <th>HSN/SAC</th>

                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Rate (BOQ)</th>
                          <th>Amount</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {groupedItems.map((group, gIndex) => (
                          <Fragment key={gIndex}>
                            {/* GROUP HEADER */}
                            <tr className="table-primary billing-report-group-row">
                              <td colSpan="9">
                                <strong className="billing-report-group-title">
                                  {group.reportType === "BOQ"
                                    ? "BOQ Items"
                                    : group.reportNumber
                                      ? `${group.reportType === "MEASUREMENT_SHEET" ? "JMR" : group.reportType} — ${group.reportNumber}`
                                      : group.reportType === "MEASUREMENT_SHEET" ? "JMR" : group.reportType}
                                </strong>

                                <span className="ms-2 billing-report-group-count">
                                  ({group.items.length} items)
                                </span>
                              </td>
                            </tr>

                            {/* ITEMS INSIDE GROUP */}
                            {group.items.map((item, i) => (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td>
                                  <Form.Control
                                    value={item.wlino || ""}
                                    readOnly
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    value={item.hsnSac || ""}
                                    readOnly
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    value={item.description}
                                    onChange={(e) =>
                                      handleLineItemChange(
                                        item.originalIndex,
                                        "description",
                                        e.target.value,
                                      )
                                    }
                                    required
                                  />
                                </td>

                                <td>
                                  <Form.Control
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) =>
                                      handleLineItemChange(
                                        item.originalIndex,
                                        "quantity",
                                        e.target.value,
                                      )
                                    }
                                    required
                                  />
                                </td>

                                <td>
                                  <Form.Control value={item.unit} disabled />
                                </td>

                                <td>
                                  <Form.Control
                                    type="number"
                                    value={item.rate}
                                    readOnly
                                  />
                                </td>

                                <td>
                                  <Form.Control value={item.amount} readOnly />
                                </td>

                                <td className="d-flex gap-1">
                                  {/* Stage Action */}
                                  {/* Stage Action — ONLY FOR STANDARD MODE */}
                                  {paymentMode === "STANDARD" && (
                                    <Button
                                      variant="warning"
                                      size="sm"
                                      onClick={() =>
                                        openStageModal(item.originalIndex)
                                      }
                                    >
                                      Stage
                                    </Button>
                                  )}

                                  {/* Delete */}
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() =>
                                      removeLineItem(item.originalIndex)
                                    }
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </Fragment>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>

                {/* FINANCIAL SUMMARY */}
                <div className="border-bottom pb-3 mb-4">
                  <h5>
                    <i className="fas fa-calculator me-2"></i>Financial Summary
                  </h5>
                  <div className="alert alert-info mb-3">
                    <strong>BOQ Total:</strong> ₹ {boqTotal.toFixed(2)} <br />
                    <strong>Stages:</strong>{" "}
                    {paymentGroups.length
                      ? Object.entries(groupStages)
                        .filter(([, s]) => s.length > 0)
                        .map(([k, s]) => {
                          const label = k === "default"
                            ? "Default"
                            : paymentGroups[parseInt(k.replace("group_", ""))]?.groupName || k;
                          return `${label}: ${s.join(", ")}`;
                        })
                        .join(" | ") || "—"
                      : `${form.stages.join(", ")} (${stagePercentage}%)`}
                    <br />
                    <strong>Max Billable Amount:</strong> ₹{" "}
                    {allowedBillingAmount}
                  </div>

                  <Row>
                    <Col md={8}></Col>
                    <Col md={4}>
                      <div className="d-flex justify-content-between mb-2">
                        <strong>Sub Total:</strong>
                        <span>₹ {form.subTotal}</span>
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <strong>CGST ({form.cgst}%):</strong>
                        <span>₹ {form.cgstAmount}</span>
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <strong>SGST ({form.sgst}%):</strong>
                        <span>₹ {form.sgstAmount}</span>
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <strong>IGST ({form.igst}%):</strong>
                        <span>₹ {form.igstAmount}</span>
                      </div>

                      <div className="d-flex justify-content-between border-top pt-2">
                        <strong>Total Amount:</strong>
                        <strong className="text-primary">
                          ₹ {form.totalAmount}
                        </strong>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* DOCUMENT & REMARKS */}
                <div className="pb-3 mb-4">
                  <h5>
                    <i className="fas fa-file-upload me-2"></i>Documents &
                    Remarks
                  </h5>

                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Upload Invoice</Form.Label>
                        <Form.Control type="file" onChange={handleFile} />
                        <FilePreview
                          file={form.invoiceDocument}
                          onRemove={() => setForm(prev => ({ ...prev, invoiceDocument: null }))}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Remarks</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="remarks"
                          value={form.remarks}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* SUBMIT BUTTON */}
                <div className="d-flex gap-2">
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? "Saving..." : "Create Billing"}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() =>
                      navigate(
                        workOrderFromUrl
                          ? `/billings?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                          : "/billings",
                      )
                    }
                  >
                    Cancel
                  </Button>

                  <Button variant="info" onClick={generateInvoiceNumber}>
                    Regenerate Invoice Number
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Modal
        show={showStageModal}
        onHide={() => setShowStageModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Direct Stage Billing</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Check
              type="checkbox"
              label="Direct Installation"
              checked={directStages.installation}
              onChange={(e) =>
                setDirectStages({
                  installation: e.target.checked,
                  tc: false,
                  handover: false,
                })
              }
            />

            <Form.Check
              type="checkbox"
              label="Direct Testing & Commissioning"
              checked={directStages.tc}
              onChange={(e) =>
                setDirectStages({
                  installation: false,
                  tc: e.target.checked,
                  handover: false,
                })
              }
            />

            <Form.Check
              type="checkbox"
              label="Direct Handover"
              checked={directStages.handover}
              onChange={(e) =>
                setDirectStages({
                  installation: false,
                  tc: false,
                  handover: e.target.checked,
                })
              }
            />
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStageModal(false)}>
            Cancel
          </Button>

          <Button variant="primary" onClick={applyDirectStages}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default BillingAdd;
